import { getAccountInsight } from "../Api/CalendarEndpoints/getAccountInsight";
import { getAsset } from "../Api/CalendarEndpoints/getAsset";
import { getContactIntelligence } from "../Api/CalendarEndpoints/getContactIntelligence";
import { getIndustryInsight } from "../Api/CalendarEndpoints/getIndustryInsight";
import { INSIGHT_CATEGORY, type IContactIntelligence, type IInsight } from "../Types/account";
import type { IAsset } from "../Types/assets";
import type {
  ICalendarAccountInsight,
  ICalendarContactIntelligence,
  ICalendarIndustryInsight,
} from "../Types/calendar";

export type WorkspaceInsightContext = {
  title: string;
  synopsis: string | null;
  notes: string[];
  sourceUrl: string | null;
};

export type WorkspaceInsightCategory =
  | INSIGHT_CATEGORY
  | "CONTACT_INTELLIGENCE";

function nonEmptyStrings(values: Array<string | null | undefined>): string[] {
  return values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
}

/** Title for workspace context — never uses synopsis (shown separately below). */
function getWorkspaceInsightTitle(insight: {
  short_summary?: string | null;
  link_text?: string | null;
}): string {
  return (
    insight.short_summary?.trim() ||
    insight.link_text?.trim() ||
    "Untitled insight"
  );
}

export function mapInsightToContext(insight: {
  short_summary?: string | null;
  link_text?: string | null;
  synopsis?: string | null;
  notes?: string[] | null;
  link_url?: string | null;
}): WorkspaceInsightContext {
  return {
    title: getWorkspaceInsightTitle(insight),
    synopsis: insight.synopsis?.trim() || null,
    notes: nonEmptyStrings(insight.notes ?? []),
    sourceUrl: insight.link_url?.trim() || null,
  };
}

export function mapAccountInsightToContext(
  insight: ICalendarAccountInsight
): WorkspaceInsightContext {
  return mapInsightToContext(insight);
}

export function mapIndustryInsightToContext(
  insight: ICalendarIndustryInsight
): WorkspaceInsightContext {
  return mapInsightToContext(insight);
}

export function mapAssetToContext(asset: IAsset): WorkspaceInsightContext {
  return {
    title: asset.short_summary?.trim() || asset.title?.trim() || "Asset",
    synopsis: asset.summary?.trim() || asset.description?.trim() || null,
    notes: nonEmptyStrings(asset.key_points ?? []),
    sourceUrl: asset.link_url?.trim() || asset.file_url?.trim() || null,
  };
}

export function mapContactIntelligenceToContext(
  contact: Pick<IContactIntelligence, "name" | "title" | "summary" | "linkedin_url">
): WorkspaceInsightContext {
  const summary = nonEmptyStrings(contact.summary ?? []);
  const titleParts = nonEmptyStrings([contact.name, contact.title]);
  return {
    title: titleParts.join(" — ") || "Contact",
    synopsis: summary[0] ?? null,
    notes: summary.slice(1),
    sourceUrl: contact.linkedin_url?.trim() || null,
  };
}

export function mapContactToContext(
  contact: ICalendarContactIntelligence
): WorkspaceInsightContext {
  return mapContactIntelligenceToContext(contact);
}

export type WorkspaceInsightContextSources = {
  insights?: IInsight[];
  assets?: IAsset[];
  contacts?: IContactIntelligence[];
};

/** Build workspace header context from data the launching page already has. */
export function resolveWorkspaceInsightContext(
  insightId: number,
  category: WorkspaceInsightCategory,
  sources: WorkspaceInsightContextSources
): WorkspaceInsightContext | null {
  if (category === INSIGHT_CATEGORY.ASSET) {
    const asset = sources.assets?.find((item) => item.id === insightId);
    return asset ? mapAssetToContext(asset) : null;
  }

  if (category === "CONTACT_INTELLIGENCE") {
    const contact = sources.contacts?.find((item) => item.id === insightId);
    return contact ? mapContactIntelligenceToContext(contact) : null;
  }

  const insight = sources.insights?.find((item) => item.id === insightId);
  return insight ? mapInsightToContext(insight) : null;
}

export function fetchWorkspaceInsightContext(
  insightId: number,
  category: WorkspaceInsightCategory,
  onSuccess: (context: WorkspaceInsightContext) => void,
  onError: () => void
): void {
  const id = String(insightId);

  if (category === INSIGHT_CATEGORY.ACCOUNT) {
    getAccountInsight(
      id,
      (insight) => onSuccess(mapAccountInsightToContext(insight)),
      onError
    );
    return;
  }

  if (category === INSIGHT_CATEGORY.INDUSTRY) {
    getIndustryInsight(
      id,
      (insight) => onSuccess(mapIndustryInsightToContext(insight)),
      onError
    );
    return;
  }

  if (category === INSIGHT_CATEGORY.ASSET) {
    getAsset(id, (asset) => onSuccess(mapAssetToContext(asset)), onError);
    return;
  }

  if (category === "CONTACT_INTELLIGENCE") {
    getContactIntelligence(
      id,
      (contact) => onSuccess(mapContactToContext(contact)),
      onError
    );
    return;
  }

  onError();
}
